<script lang="ts">
  import {
    Form,
    TextField,
    Select,
    SelectItem,
    Submit,
    Checkbox,
    TextArea,
    Button,
    Flex,
    FlexItem,
  } from "@gl/design-system"
  import { format, subMinutes, parseISO, parseJSON } from "date-fns"
  import { toZonedTime } from "date-fns-tz"
  import { onMount } from "svelte"
  import { _ } from "svelte-i18n"
  import { getChannel, getChannels, getJob, getJobsStatus, simplecut } from "../services/endpoints"
  import {
    MediaType,
    type Channel,
    type JobStatus,
    type Pagination,
    type Simplecut,
    type Stream,
  } from "../services/models"
  import dayjs from "dayjs"
  import customParseFormat from "dayjs/plugin/customParseFormat"
  import { navigateCustom } from "../utils/navigateCustom"
  import KeyValue from "./KeyValue.svelte"
  import Destination from "./Destination.svelte"
  import { Search } from "."
  import { ChannelsMergedTimeline } from "../pages"
  import axios from "axios"
  import jsonWorker from "monaco-editor/esm/vs/language/json/json.worker?worker"
  import type { editor } from "monaco-editor"
  dayjs.extend(customParseFormat)

  const audioCodes = [
    "qbf",
    "qbd",
    "qbb",
    "qbc",
    "qbe",
    "qaf",
    "qax",
    "qat",
    "qay",
    "qaq",
    "qav",
    "qan",
    "qai",
    "qao",
    "qak",
    "qad",
    "qaw",
    "qba",
    "qau",
    "qap",
    "qas",
    "qam",
    "qal",
    "qah",
    "qag",
    "qae",
    "qaz",
    "qab",
    "qaa",
    "qaj",
    "qar",
    "qac",
  ]
  const timeFormat = "yyyy-MM-dd'T'HH:mm:ss.SSS"
  let startDateTime = format(new Date(subMinutes(Date.now(), 30)), timeFormat)
  let endDateTime = format(new Date(Date.now()), timeFormat)
  let webhookEditor: editor.IStandaloneCodeEditor
  let webhookEditorElm: HTMLElement
  const form: Simplecut = {
    title: "",
    channel: "",
    video: "all",
    audio: "all",
    exportStrategy: "highest_with_audio",
    frameAccurate: false,
    fastStart: true,
    skipGapSeconds: 1,
    locationFilter: {},
    audioLanguageRewrite: {},
    startDateTime: null,
    endDateTime: null,
    destinations: [],
    webhooks: [],
  }

  let webhooks = []

  let eupCMSEndpoint = ""
  let eupCMSCommonID = ""

  let channelSearchText = ""
  let channels: Pagination<Channel>

  let jobSearchText = ""
  let jobs: Pagination<JobStatus>
  let selectedJob: string

  const makeCut = () => {
    form.startDateTime = toZonedTime(parseISO(startDateTime), Intl.DateTimeFormat().resolvedOptions().timeZone)
    form.endDateTime = toZonedTime(parseISO(endDateTime), Intl.DateTimeFormat().resolvedOptions().timeZone)

    simplecut(form)
      .then((job) => navigateCustom("/jobs"))
      .catch((resp) => alert(JSON.stringify(resp.response.data)))
  }

  onMount(async () => {
    searchChannels()
    searchJobs()
    await setupEditors()
  })

  async function setupEditors() {
    self.MonacoEnvironment = {
      getWorker: function (_moduleId, label) {
        return new jsonWorker()
      },
    }
    const monaco = await import("monaco-editor")

    monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
      validate: true,
      schemas: [
        {
          uri: "http://json-schema.org/draft-04/schema#",
          fileMatch: ["*"],
          schema: {
            type: "array",
            $schema: "http://json-schema.org/draft-04/schema#",
            description: "",
            minItems: 0,
            uniqueItems: true,
            items: {
              type: "object",
              required: ["url"],
              properties: {
                url: {
                  type: "string",
                  minLength: 10,
                },
                headers: {
                  type: "object",
                },
                notifyWhen: {
                  type: ["array", "null"],
                  items: {
                    enum: [
                      "created",
                      "enqueued",
                      "generating",
                      "failed_generating",
                      "finished_generating",
                      "uploading",
                      "finished",
                      "failure",
                      "canceled",
                    ],
                    type: "string",
                    properties: {},
                  },
                },
              },
            },
          },
        },
      ],
    })

    webhookEditor = monaco.editor.create(webhookEditorElm, {
      value: JSON.stringify(webhooks, null, 2),
      language: "json",
      theme: "default",
    })

    webhookEditor.onDidChangeModelContent(() => {
      try {
        form.webhooks = JSON.parse(webhookEditor.getValue())
      } catch {}
    })
    return () => {
      webhookEditor.dispose()
    }
  }

  function searchChannels() {
    getChannels(10, 0, channelSearchText).then((c) => {
      channels = c
    })
  }

  function searchJobs() {
    getJobsStatus(10, 0, jobSearchText).then((j) => {
      jobs = j
    })
  }

  function loadJobData() {
    getJob(selectedJob).then((job) => {
      form.title = job.title
      form.channel = job.cuts[0]?.mediastore.uuid
      channelSearchText = form.channel
      searchChannels()

      // just assuming it was a Simplecut
      let start = parseJSON(job.cuts[0]?.start)
      let end = parseJSON(job.cuts[job.cuts.length - 1]?.end)
      form.startDateTime = format(start, timeFormat)
      startDateTime = format(start, timeFormat)
      form.endDateTime = format(end, timeFormat)
      endDateTime = format(end, timeFormat)
      form.exportStrategy = job.exportStrategy
      form.frameAccurate = job.frameAccurate
      form.fastStart = job.fastStart
      form.skipGapSeconds = job.skipGapSeconds
      form.destinations = job.destinations
      form.webhooks = job.webhooks
      webhooks = job.webhooks
      webhookEditor.setValue(JSON.stringify(job.webhooks, null, 2))
      form.audioLanguageRewrite = job.audioLanguageRewrite
      form.locationFilter = job.locationFilter

      if (job.metadata?.simpleCutRequest) {
        form.audio = job.metadata.simpleCutRequest.audio
        form.video = job.metadata.simpleCutRequest.video
      }
    })
  }

  function getSelectedChannelData() {
    return channels.list.find((c) => c.uuid == form.channel)
  }

  function setStartAndEndToChannelTimes() {
    const channel = getSelectedChannelData()
    if (!channel) return

    const timeline = Object.values(channel.timelines)
      .flatMap((t) => [t.startDateTime, t.endDateTime])
      .sort((a, b) => parseJSON(a).getTime() - parseJSON(b).getTime())

    if (timeline.length == 0) return

    const start = timeline[0]
    const end = timeline[timeline.length - 1]

    startDateTime = format(parseJSON(start), timeFormat)
    endDateTime = format(parseJSON(end), timeFormat)
  }

  function loadEbsAudioRewrite() {
    // Only for EUP relevant
    form.audioLanguageRewrite = {
      qbf: "or",
      qbd: "en",
      qbb: "fr",
      qbc: "de",
      qbe: "it",
      qaf: "es",
      qax: "el",
      qat: "pt",
      qay: "nl",
      qaq: "da",
      qav: "fi",
      qan: "sv",
      qai: "cs",
      qao: "et",
      qak: "lv",
      qad: "lt",
      qaw: "hu",
      qba: "mt",
      qau: "pl",
      qap: "sk",
      qas: "sl",
      qam: "bg",
      qal: "ro",
      qah: "hr",
      qag: null,
      qae: null,
      qaz: null,
      qab: null,
      qaa: null,
      qaj: null,
      qar: null,
      qac: null,
    }
  }

  function loadAvailableLanguagesForAudioRewrite() {
    form.audioLanguageRewrite = {}
    if (!form.channel) {
      alert("No channel selected")
      return
    }
    getChannel(form.channel).then((channel) => {
      channel.streams.forEach((stream) => {
        stream = <Stream>stream
        if (stream.type != MediaType.AUDIO) return

        if (stream.value.language) {
          form.audioLanguageRewrite[stream.value.language] = stream.value.language
        }
      })
    })
  }

  function loadAudioMappingFromEupCms() {
    if (!eupCMSCommonID || !eupCMSEndpoint) return

    axios
      .get(`${eupCMSEndpoint}${eupCMSCommonID}`)
      .then((cmsResponse) => {
        form.audioLanguageRewrite = {}
        cmsResponse.data.languageMapping?.forEach((mapping, idx) => {
          if (mapping == null) return
          form.audioLanguageRewrite[audioCodes[idx]] = mapping.lang
        })
      })
      .catch((err) => {
        alert(JSON.stringify(err))
      })
  }
</script>

<div class="job-selection">
  <Flex style="align-items: end;">
    <FlexItem style="margin-right: var(--gl-spacing-2); flex-grow: 1;">
      <Select bind:value={selectedJob} label={$_("common.jobs")}>
        {#if jobs}
          <SelectItem />
          {#each jobs.list as job}
            <SelectItem bind:label={job.title} bind:value={job.uuid} />
          {/each}
        {/if}
      </Select>
    </FlexItem>
    <FlexItem style="margin-right: 3rem">
      <Button on:click={loadJobData}>Load Job-Data</Button>
    </FlexItem>
    <FlexItem>
      <Search placeholder="Search jobs..." bind:searchText={jobSearchText} on:search={searchJobs} />
    </FlexItem>
  </Flex>
</div>
<hr />
<Form>
  <TextField label={"Job " + $_("common.name")} bind:value={form.title} required={$_("validations.required")} />
  <div class="channel-selection">
    <Flex>
      <FlexItem style="margin-right: 3rem; flex-grow: 1;">
        <Select
          bind:value={form.channel}
          label={$_("common.channels")}
          required={$_("validations.required")}
          on:change={setStartAndEndToChannelTimes}
        >
          {#if channels}
            <SelectItem />
            {#each channels.list as channel}
              <SelectItem bind:label={channel.name} bind:value={channel.uuid} />
            {/each}
          {/if}
        </Select>
      </FlexItem>
      <FlexItem>
        <Search placeholder="Search channels..." bind:searchText={channelSearchText} on:search={searchChannels} />
      </FlexItem>
    </Flex>

    <Select bind:value={form.video} label={$_("common.video")} required={$_("validations.required")}>
      <SelectItem value="all" />
      <SelectItem value="high" />
      <SelectItem value="medium" />
      <SelectItem value="low" />
      <SelectItem value="none" />
    </Select>

    <Select bind:value={form.audio} label={$_("common.audio")} required={$_("validations.required")}>
      <SelectItem value="all" />
      <SelectItem value="first" />
      <SelectItem value="und" />
      <SelectItem value="or" />
      <SelectItem value="none" />
    </Select>

    <TextField label="SkipGapSeconds" bind:value={form.skipGapSeconds} variant="Number" />

    <div style="display: flex; justify-content: space-between; margin-bottom: 1rem">
      <div>
        <label for="start"><b>Start</b></label>
        <input
          type="datetime-local"
          step="0.001"
          id="start"
          class="gl-input"
          required={true}
          bind:value={startDateTime}
        />
      </div>
      <div>
        <label for="end"><b>End</b></label>
        <input type="datetime-local" step="0.001" id="end" class="gl-input" required={true} bind:value={endDateTime} />
      </div>
    </div>

    {#if form.channel}
      <ChannelsMergedTimeline
        channelUuid={form.channel}
        audioSelection={form.audio}
        videoSelection={form.video}
        skipGapSeconds={form.skipGapSeconds}
        startTime={startDateTime}
        endTime={endDateTime}
      ></ChannelsMergedTimeline>
    {/if}
  </div>

  <div class="group">
    <h2 class="group-header">Export options</h2>
    <Select label="Export Strategy" bind:value={form.exportStrategy}>
      <SelectItem label="Höchste mit Audio" value="highest_with_audio" />
      <SelectItem label="Default" value="default" />
    </Select>
    <Checkbox label="FrameAccurate (Currently not working properly)" bind:value={form.frameAccurate} />
    <Checkbox label="FastStart" bind:value={form.fastStart} />
  </div>
  <div class="group">
    <h2 class="group-header">Location filter</h2>
    <KeyValue bind:keyValues={form.locationFilter} keyPlaceholder="e.g. env" valuePlaceholder="e.g. prod"></KeyValue>
  </div>

  <div class="group">
    <h2 class="group-header">Audio Language Rewrite</h2>
    <div>
      <Button on:click={loadAvailableLanguagesForAudioRewrite}>Load available languages</Button>
      <Button on:click={loadEbsAudioRewrite}>Load EBS</Button>
    </div>
    <Flex style="align-items: end; margin-top: 1rem">
      <FlexItem>
        <Select label="Endpoint" bind:value={eupCMSEndpoint}>
          <SelectItem value="https://control.eup.glcloud.eu/content-manager/api/v1/event/content/" />
          <SelectItem value="https://control-stage.eup.glcloud.eu/content-manager/api/v1/event/content/" />
        </Select>
      </FlexItem>
      <FlexItem style="flex-grow: 1;">
        <TextField label="CommonID" bind:value={eupCMSCommonID} />
      </FlexItem>
      <FlexItem>
        <Button on:click={loadAudioMappingFromEupCms}>Load Audio mapping from CMS</Button>
      </FlexItem>
    </Flex>
    <hr />
    <KeyValue bind:keyValues={form.audioLanguageRewrite} keyPlaceholder="from" valuePlaceholder="to"></KeyValue>
  </div>

  <div class="group">
    <h2 class="group-header">Upload destinations</h2>
    <Destination bind:destinations={form.destinations} readonly={false}></Destination>
  </div>

  <div class="group">
    <h2 class="group-header">Webhooks</h2>
    <div bind:this={webhookEditorElm} style="width: 100%; height: 20rem;"></div>
  </div>
  <hr />
  <Flex style="justify-content: end">
    <Submit on:submit={makeCut} />
  </Flex>
</Form>

<style>
  .group,
  .channel-selection {
    padding: 1rem;
    margin-bottom: 1rem;
    border-radius: 5px;
    border: 1px solid lightgray;
  }

  .channel-selection :global(.gl-select),
  .job-selection :global(.gl-select) {
    width: 100%;
  }

  .group .group-header {
    font-weight: bold;
    margin-bottom: 1rem;
    border-bottom: 1px solid lightgray;
    padding-bottom: 1rem;
  }
</style>
